import { useParams } from 'react-router';
import {
  ApiError,
  CookieUtil,
  GoneError,
  useApi,
  useApiWithErrors,
} from '@axiom/ui';
import type { RolesType } from '@axiom/const';
import { CandidatesConst } from '@axiom/const';
import type {
  Account,
  AfcSubmission,
  AfcSubmissionCandidateOpportunity,
} from '@axiom/validation';

import { CandidateApi } from '../api/candidate-api';
import { EngagementTalentUtil } from '../components/EngagementTalent/engagement-talent-util';
import { InstantMatchesApi } from '../api/instant-matches-api';
import { CandidateLegalTechSkillsApi } from '../api/candidate-legal-tech-skills-api';
import { SubmissionApi } from '../api/submission-api';
import { UserApi } from '../api/user-api';

const { roleDisplayOrder, sortOrderObject } = EngagementTalentUtil;

interface Args {
  account?: Account;
  submissionId?: AfcSubmission['id'];
}

export const useEngagementTalentData = (props: Args = {}) => {
  const params = useParams();
  const { talentSlug } = params;
  const urlSubmissionId = params.submissionId;
  const urlAccountId = params.accountId;

  const accountId = props.account?.id ?? urlAccountId;
  const submissionId = props.submissionId ?? urlSubmissionId;
  if (!submissionId) {
    throw new Error('No submissionId provided');
  }
  const isUserLoggedIn = CookieUtil.hasUserCookie() || null;
  const [response, { data: user } = { data: null }] = useApiWithErrors(
    SubmissionApi.readSubmissionDetails(submissionId),
    isUserLoggedIn ? UserApi.readUser() : undefined
  );

  let invalidSubmissionRequest = false;
  if (response instanceof ApiError) {
    if (response instanceof GoneError) {
      invalidSubmissionRequest = true;
    } else {
      throw response;
    }
  }

  const submission = response.data;

  const sortedPositions =
    submission?.positions.sort((a, b) =>
      !!a.role && !!b.role
        ? sortOrderObject[a.role] - sortOrderObject[b.role]
        : 0
    ) ?? [];
  const interviewingStatus = CandidatesConst.CandidateStatuses.Interviewing;

  sortedPositions.forEach(position => {
    position.candidateOpportunities.sort((a, b) => {
      if (
        a.candidateStatus === interviewingStatus &&
        b.candidateStatus !== interviewingStatus
      ) {
        return -1;
      } else if (
        a.candidateStatus !== interviewingStatus &&
        b.candidateStatus === interviewingStatus
      ) {
        return 1;
      }
      return 0;
    });
  });

  // instantMatches ================================ START
  const [
    { data: { matches: instantMatches } } = {
      data: { matches: [] },
    },
  ] = useApi(InstantMatchesApi.readInstantMatches(submissionId));

  const availableInstantMatches = instantMatches.filter(match => !match.passed);
  const instantMatchCandidateOpportunities = availableInstantMatches.reduce<
    AfcSubmissionCandidateOpportunity[]
  >((acc, match) => {
    const isCandidateAlreadyPresent =
      sortedPositions[0]?.candidateOpportunities.some(
        candidateOpportunity =>
          candidateOpportunity.candidate.id === match.candidate.id
      );

    if (!isCandidateAlreadyPresent) {
      acc.push({
        candidate: match.candidate,
        badge: null,
        displayBillingRate: 'hourly',
        pinnedByClient: match.pinnedByClient,
        proposedHourlyRate: match.calculatedRate || match.rate,
        isInstantMatch: true,
        id: '',
        candidateStatus: null,
        submissionDescription: null,
        showCandidateReputation: false,
        interviewRequested: null,
        candidateReputationClarification: null,
      });
    }

    return acc;
  }, []);

  if (availableInstantMatches.length > 0 && sortedPositions.length === 1) {
    sortedPositions[0]?.candidateOpportunities.push(
      ...instantMatchCandidateOpportunities
    );
  }

  // instantMatches ================================ END

  // groupedCandidateOpps ================================ START
  const filterPositionByRole = (role: RolesType) =>
    sortedPositions
      .filter(position => position.role === role)
      .flatMap(position => position.candidateOpportunities);
  const groupedCandidateOpps = roleDisplayOrder
    .map(order => ({
      title: order,
      data: filterPositionByRole(order),
    }))
    .filter(item => item.data.length > 0);
  // groupedCandidateOpps ================================ END

  // allCandidate ================================ START
  const allCandidateOpps = sortedPositions.flatMap(
    p => p.candidateOpportunities
  );
  // allCandidate ================================ END

  // candidate ================================ START
  const candidateOppFromSlug = talentSlug
    ? allCandidateOpps.find(
        candidateOpp => candidateOpp.candidate.id === talentSlug
      )
    : undefined;

  const firstPinnedCandidateOpp = allCandidateOpps.find(
    candidateOpp => candidateOpp.pinnedByClient
  );

  const candidateOpp =
    candidateOppFromSlug ?? firstPinnedCandidateOpp ?? allCandidateOpps[0];
  // candidate ================================ END

  // groupedExperiences & legalTechSkills for a candidate ================================ START
  const [
    { data: groupedExperiences } = { data: [] },
    { data: legalTechSkills } = { data: [] },
  ] = useApi(
    candidateOpp?.candidate.id
      ? CandidateApi.readCandidateGroupedExperiences(
          candidateOpp.candidate.id,
          {
            submissionId,
          }
        )
      : undefined,
    candidateOpp?.candidate.id
      ? CandidateLegalTechSkillsApi.readLegalTechSkills(
          candidateOpp.candidate.id
        )
      : undefined
  );
  const matchesCount =
    submission?.positions.flatMap(position => position.candidateOpportunities)
      .length ?? 0;
  // groupedExperiences & legalTechSkills for a candidate ================================ END
  return {
    accountId,
    allCandidateOpps,
    candidateOpp,
    candidateOppFromSlug,
    groupedCandidateOpps,
    groupedExperiences,
    invalidSubmissionRequest,
    legalTechSkills,
    submission,
    talentSlug,
    user,
    matchesCount,
    instantMatches,
  };
};
